import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Tab,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import Tabs from '@mui/material/Tabs';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import DrawerComp from './DrawerComp';
import logo from './SliteBadhotell.png';
import { useTranslation } from '../hooks/useTranslation';
import LanguageSwitcher from './LanguageSwitcher';
import MobileLanguageSwitcher from './MobileLanguageSwitcher';

const Navbar = ({ menuItems }) => {
  const theme = useTheme();
  const isMatch = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const getTabValueFromPath = (pathname) => {
    if (pathname === '/') return 'Hem';
    const tab = pathname.replace('/', '');
    return tab.charAt(0).toUpperCase() + tab.slice(1);
  };
  
  const [value, setValue] = useState(getTabValueFromPath(location.pathname));

  // Update tab value when location changes
  useEffect(() => {
    setValue(getTabValueFromPath(location.pathname));
  }, [location.pathname]);

  // Map Swedish menu items to translation keys
  const getTranslationKey = (swedishText) => {
    const translationMap = {
      'Hem': 'nav.home',
      'Boende': 'nav.rooms', 
      'Frukost': 'nav.breakfast',
      'Galleri': 'nav.gallery',
      'Restaurang': 'nav.restaurant',
      'Boka': 'nav.book',
      'Faq': 'nav.faq'
    };
    return translationMap[swedishText] || swedishText;
  };

  // Handle tab click
  const handleTabClick = (link, e) => {
    if (link === 'Restaurang') {
      e.preventDefault();
      window.open('https://slitebaren.se/', '_blank', 'noopener,noreferrer');
      // Reset selected tab to home after a short delay
      setTimeout(() => {
        if (location.pathname === '/') {
          setValue('Hem');
        }
      }, 100);
    } else {
      // For internal tabs
      const routeMap = {
        'Hem': '/',
        'Boende': '/boende',
        'Frukost': '/frukost',
        'Galleri': '/galleri',
        'Boka': '/boka',
        'Faq': '/faq'
      };
      if (routeMap[link]) {
        setValue(link);
        navigate(routeMap[link]);
      }
    }
  };

  return (
    <AppBar sx={{ 
      background: 'none', 
      position: 'fixed',
      width: '100%'
    }}>
      <Toolbar sx={{ 
        background: 'rgb(9 159 190/0.8)',
        minHeight: { xs: '56px', md: '64px' },
        padding: { xs: '0 8px', md: '0 16px' }        
      }}>
        {isMatch ? (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            width: '100%', 
            justifyContent: 'space-between',
            gap: 0.5
          }}>
            {/* Smaller logo/text for mobile */}
            <Typography sx={{ 
              color: 'white', 
              fontSize: '0.8rem',
              flexShrink: 0
            }}>
              <strong>Slite</strong>
              <br />
              <strong>Badhotell</strong>
            </Typography>
            
            <picture>
              <img
                src={logo}
                style={{
                  width: '18vw',
                  maxWidth: '70px',
                  filter: 'invert(99%) sepia(1%) saturate(1365%) hue-rotate(128deg) brightness(118%) contrast(101%)',
                }}
                alt="Slite Badhotell Logo"
              />
            </picture>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              flexShrink: 0
            }}>
              <MobileLanguageSwitcher />
              <DrawerComp links={menuItems} />
            </Box>
          </Box>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            width: '100%',
            justifyContent: 'space-between',
            flexWrap: 'nowrap',
            gap: 0.5
          }}>
            {/* Logo/Title Section - Compact */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              minWidth: '140px',
              maxWidth: '140px',
              flexShrink: 0,
            }}>
              <Typography sx={{ 
                fontSize: '0.85rem',
                whiteSpace: 'nowrap',
                color: 'white'
              }}>
                <strong>Slite Badhotell</strong>
              </Typography>
            </Box>

            {/* Navigation Tabs*/}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              flex: 1,
              minWidth: 0,
              mx: 0.5,
              overflow: 'hidden',
            }}>
              <Tabs
                value={value}
                onChange={(e, val) => setValue(val)}
                sx={{
                  minHeight: '44px',
                  '& .MuiTabs-scroller': {
                    overflow: 'hidden !important',
                  },
                  '& .MuiTabs-flexContainer': {
                    gap: '0px', 
                  }
                }}
                variant="scrollable"
                scrollButtons="auto"
              >
                {menuItems.map((link, index) => (
                   
                  link === "Restaurang" ? (
                    // External link tab for Restaurang
                    <Tab
                      key={index}
                      value={link}
                      label={
                        <strong style={{ 
                          fontSize: '1rem',
                          whiteSpace: 'nowrap'
                        }}>
                          {t(getTranslationKey(link))}
                        </strong>
                      }
                      onClick={(e) => handleTabClick(link, e)}
                      sx={{
                        textTransform: 'initial',
                        minWidth: 'auto',
                        px: 1.2,
                        py: 0.5,
                        fontSize: '1rem',
                        color: 'White',
                        '&.Mui-focusVisible': {
                          color: '#384955',
                        },
                        '&:focus': {
                          color: '#384955',
                        },
                        opacity: 1,
                        minHeight: '36px',
                        '&:hover': {
                          opacity: 0.9,
                        }
                      }}
                    />
                  ) : (
                    // Internal link tabs for others
                    <Tab
                      key={index}
                      value={link}
                      label={
                        <strong style={{ 
                          fontSize: '1rem',
                          whiteSpace: 'nowrap'
                        }}>
                          {t(getTranslationKey(link))}
                        </strong>
                      }
                      component={Link}
                      to={
                        link === 'Hem' ? '/' :
                        link === 'Boende' ? '/boende' :
                        link === 'Frukost' ? '/frukost' :
                        link === 'Galleri' ? '/galleri' :
                        link === 'Boka' ? '/boka' :
                        '/faq'
                      }
                      sx={{
                        textTransform: 'initial',
                        minWidth: 'auto',
                        px: 1.2,
                        py: 0.5,
                        fontSize: '0.8rem',
                        color: 'White',
                        '&.Mui-focusVisible': {
                          color: '#384955',
                          outlineOffset: '2px',
                        },
                        '&:focus': {
                          color: '#384955',
                          outlineOffset: '2px',
                        },
                        opacity: 1,
                        minHeight: '36px',
                        '&:hover': {
                          opacity: 0.9,
                        }
                      }}
                    />
                  )
                ))}
              </Tabs>
            </Box>

            {/* Language Switcher - Compact */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'flex-end',
              minWidth: '80px',
              flexShrink: 0,
            }}>
              <LanguageSwitcher />
            </Box>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;